import { reactive } from 'vue';

// Global dialog state
const globalDialogState = reactive({
    isOpen: false,
    dialogType: 'confirm',
    dialogProps: {},
    confirmCallback: null,
    cancelCallback: null
});

// Dialog management functions
const showDialog = async (type, props = {}) => {
    return new Promise((resolve, reject) => {
        globalDialogState.dialogType = type;
        globalDialogState.dialogProps = props;
        globalDialogState.confirmCallback = resolve;
        globalDialogState.cancelCallback = reject;
        globalDialogState.isOpen = true;
    });
};

const handleConfirm = (result) => {
    globalDialogState.isOpen = false;
    if (globalDialogState.confirmCallback) {
        const callback = globalDialogState.confirmCallback;
        // Clear all dialog state to prevent state persistence
        globalDialogState.confirmCallback = null;
        globalDialogState.cancelCallback = null;
        globalDialogState.dialogProps = {};
        callback(result);
    }
};

const handleCancel = (result) => {
    globalDialogState.isOpen = false;
    if (globalDialogState.cancelCallback) {
        const callback = globalDialogState.cancelCallback;
        // Clear all dialog state to prevent state persistence
        globalDialogState.confirmCallback = null;
        globalDialogState.cancelCallback = null;
        globalDialogState.dialogProps = {};
        callback(result);
    }
};

const handleUpdateOpen = (open) => {
    globalDialogState.isOpen = open;
    if (!open && globalDialogState.cancelCallback) {
        const callback = globalDialogState.cancelCallback;
        // Clear all dialog state to prevent state persistence
        globalDialogState.confirmCallback = null;
        globalDialogState.cancelCallback = null;
        globalDialogState.dialogProps = {};
        callback(false);
    }
};

// Legacy API functions using global state
export function useConfirmBeforeAction(actionType = 'delete', action, props = {}, onCancelAction) {
    // Determine if input is required based on action type or explicit props
    const defaultRequiresInput = actionType === 'delete' || actionType === 'reset';
    const requiresInput = props.requiresInput !== undefined ? props.requiresInput : defaultRequiresInput;
    
    // Generate default expected input, but allow override via props
    const defaultExpectedInput = actionType === 'delete' ? 'delete me' : `${actionType} it`;
    const expectedInput = props.expectedInput !== undefined ? props.expectedInput : defaultExpectedInput;

    const dialogOptions = {
        type: actionType,
        requiresInput,
        expectedInput,
        inputPlaceholder: props.inputPlaceholder || `Type "${expectedInput}" to confirm`,
        confirmText: actionType === 'delete' ? 'Delete' : 'Confirm',
        ...props
    };

    return showDialog('confirm', dialogOptions)
        .then((result) => {
            if (action) return action();
            return result;
        })
        .catch((error) => {
            if (onCancelAction) return onCancelAction();
            throw error;
        });
}

// Modern API - improved flexibility and type safety
export function useConfirmDialog(options = {}) {
    const {
        // Dialog configuration
        type = 'confirm',
        title,
        message,
        confirmText,
        cancelText = 'Cancel',

        // Input configuration
        requiresInput = type === 'delete',
        expectedInput = type === 'delete' ? 'delete me' : `${type} it`,
        inputPlaceholder,
        inputLabel,

        // Entity information (for auto-generated messages)
        entity,
        name,

        // Actions
        onConfirm,
        onCancel,

        // Advanced options
        variant = type, // 'delete', 'warning', 'info', etc.
        destructive = type === 'delete',
        ...otherProps
    } = options;

    const dialogOptions = {
        type: variant,
        title,
        message,
        confirmText: confirmText || (type === 'delete' ? 'Delete' : 'Confirm'),
        cancelText,
        requiresInput,
        expectedInput,
        inputPlaceholder: inputPlaceholder || expectedInput,
        inputLabel,
        entity,
        name,
        destructive,
        ...otherProps
    };

    return showDialog('confirm', dialogOptions)
        .then((result) => {
            if (onConfirm) return onConfirm(result);
            return result;
        })
        .catch((error) => {
            if (onCancel) return onCancel(error);
            throw error;
        });
}

export function useErrorDialog(props = {}) {
    return showDialog('error', props).catch(() => {
        // Error dialogs don't need cancel handling
    });
}

export function useConfirmBeforeCreate(action, props = {}) {
    return useConfirmBeforeAction('create', action, props);
}

export function useConfirmBeforeDelete(action, props = {}) {
    return useConfirmBeforeAction('delete', action, props);
}

export function useConfirmBeforeWarning(action, props = {}) {
    return useConfirmBeforeAction('warning', action, props);
}

// Modern convenience helpers
export function useDeleteConfirmation(options = {}) {
    return useConfirmDialog({
        type: 'delete',
        destructive: true,
        requiresInput: true,
        expectedInput: 'delete me',
        ...options
    });
}

export function useWarningConfirmation(options = {}) {
    return useConfirmDialog({
        type: 'warning',
        destructive: false,
        requiresInput: false,
        ...options
    });
}


// Plugin installation
export default {
    install(app) {
        // Provide global dialog state and functions
        // Add methods to the reactive object
        globalDialogState.showDialog = showDialog;
        globalDialogState.handleConfirm = handleConfirm;
        globalDialogState.handleCancel = handleCancel;
        globalDialogState.handleUpdateOpen = handleUpdateOpen;

        app.provide('globalDialog', globalDialogState);

        // Make dialog functions available globally
        app.config.globalProperties.$useConfirmBeforeAction = useConfirmBeforeAction;
        app.config.globalProperties.$useErrorDialog = useErrorDialog;
    }
};
